import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { Col, Form, FormControlProps, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../app/store';
import { ChangeEventHandler, ValueType } from '../../app/types';
import { useAuth } from '../signin/auth';
import styles from './EntryInput.module.css';
import { getEntry, selectEntryStateByID, writeEntry } from './measurementsSlice';
import { EntryUpdate, Measurement } from './types';


interface EntryInputProps extends FormControlProps {
  measurement: Measurement
  entryID: string
}

function EntryInput(props: EntryInputProps) {
  const auth = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const addr = {
    measurementID: props.measurement.id as string,
    entryID: props.entryID,
  };
  const entry = useSelector((state: RootState) => selectEntryStateByID(state, addr)?.entry || null);
  const status = useSelector((state: RootState) => selectEntryStateByID(state, addr)?.status || 'idle');
  const error = useSelector((state: RootState) => selectEntryStateByID(state, addr)?.error || null);
  const { measurement } = props;

  const [valueBoolean, setValueBoolean] = useState<boolean | null>(null);
  const [valueInteger, setValueInteger] = useState<number | ''>('');
  const [valueFloat, setValueFloat] = useState<number | ''>('');
  const [changeTimeout, setChangeTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isUnset, setIsUnset] = useState<boolean>(false);
  const validIntegers = props.measurement.validIntegers || [];

  useEffect(() => {
    if (status === 'idle') {
      if (auth.authToken) {
        dispatch(getEntry({
          authToken: auth.authToken,
          entry: {
            measurementID: measurement.id as string,
            entryID: props.entryID,
          }
        }));
      } else {
        console.log('No auth token!');
      }
    }
  }, [status, dispatch, measurement.id, props.entryID, auth.authToken])

  useEffect(() => {
    if (entry) {
      setValueInteger((entry.valueInteger === null) ? '' : entry.valueInteger);
      setValueFloat((entry.valueFloat === null) ? '' : entry.valueFloat);
      setValueBoolean(entry?.valueBoolean);
      switch (measurement.type) {
        case ValueType.Boolean:
          setIsUnset(entry.valueBoolean === null);
          break;
        case ValueType.Float:
          setIsUnset(entry.valueFloat === null);
          break;
        case ValueType.Integer:
          setIsUnset(entry.valueInteger === null);
          break;
      }
    }
  }, [entry, measurement.type]);

  const onChange: ChangeEventHandler = (e) => {
    if (!measurement) {
      return;
    }
    if (changeTimeout) {
      clearTimeout(changeTimeout);
      setChangeTimeout(null);
    }
    const newEntry: EntryUpdate = {
      ...entry,
      id: props.entryID,
      type: measurement.type,
    };
    switch (measurement.type) {
      case ValueType.Boolean:
        switch (e.target.value) {
          case 'true':
            setValueBoolean(true);
            newEntry.valueBoolean = true;
            setIsUnset(false);
            break;
          case 'false':
            setValueBoolean(false);
            newEntry.valueBoolean = false;
            setIsUnset(false);
            break;
          case 'unset':
            setValueBoolean(null);
            setIsUnset(true);
            newEntry.valueBoolean = null;
        }
        break;
      case ValueType.Integer:
        const intValue = parseInt(e.target.value);
        if (isNaN(intValue)) {
          setValueInteger('');
          setIsUnset(true);
          newEntry.valueInteger = null;
        } else {
          newEntry.valueInteger = intValue;
          setValueInteger(intValue);
          setIsUnset(false);
        }
        break;
      case ValueType.Float:
        const floatValue = parseFloat(e.target.value);
        if (isNaN(floatValue)) {
          setValueFloat('');
          setIsUnset(true);
          newEntry.valueFloat = null;
        } else {
          newEntry.valueFloat = floatValue
          setValueFloat(floatValue);
          setIsUnset(false);
        }
        break;
    }
    setChangeTimeout(setTimeout(() => {
      if (auth.authToken) {
        dispatch(writeEntry({
          authToken: auth.authToken,
          measurementID: props.measurement.id as string,
          entry: newEntry,
        }));
      } else {
        console.log('No auth token!');
      }
    }, 1000));
  }
  let isDisabled = status !== 'succeeded';
  let content = null;
  switch (measurement.type) {
    case ValueType.Boolean:
      content = (
        <Row>
          <Col sm="2">
            <Form.Check type="radio" id={props.measurement.id + '-true'} value="true" checked={valueBoolean === true} onChange={onChange} disabled={isDisabled} label="True" />
          </Col>
          <Col sm="2">
            <Form.Check type="radio" id={props.measurement.id + '-false'} value="false" checked={valueBoolean === false} onChange={onChange} disabled={isDisabled} label="False" />
          </Col>
          <Col sm="2">
            <Form.Check type="radio" id={props.measurement.id + '-undefined'} value="unset" checked={valueBoolean === null} onChange={onChange} disabled={isDisabled} label="Not set" />
          </Col>
        </Row>
      );
      break;
    case ValueType.Integer:
      if (validIntegers.length > 0) {
        content = (
          <Form.Select value={valueInteger} onChange={onChange} disabled={isDisabled}>
            <option key="null" value="">Not set</option>
            {validIntegers.map(i =>
              <option key={i} value={i}>{i}</option>
            )}
          </Form.Select>
        );
      } else {
        content = (
          <Form.Control type="number" value={valueInteger} step="1" onChange={onChange} disabled={isDisabled} />
        );
      }
      break;
    case ValueType.Float:
      content = (
        <Form.Control type="number" value={valueFloat} step="0.1" onChange={onChange} disabled={isDisabled} />
      );
      break;
    default:
      content = (
        <div className="alert alert-danger" role="alert">
          Unsupported type: <code>{props.measurement.type}</code>!
        </div>
      );
  }
  const entryInputClasses = classnames(
    styles.entryInput,
    styles[`entryInput-${status}`],
    {
      [styles['entryInput-unset']]: isUnset,
    }
  );
  return (
    <Form.Group as={Row} className={entryInputClasses}>
      <Form.Label column sm="4" md="3" className={styles.entryInputLabel}>{props.measurement.name}</Form.Label>
      <Col sm="8" md="9">
        {status === 'failed' && <div className={styles.entryInputError}>{error}</div>}
        {content}
        {props.measurement.prompt &&
          <small className="form-text text-muted">{props.measurement.prompt}</small>
        }
      </Col>
    </Form.Group>
  );
};

export default EntryInput;